import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  FormGroup,
  Label,
  Input,
  Badge,
  Spinner,
  Progress,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import axios from 'axios';
import { BASE_URL } from '../constants/apiConfig';
import './ReviewApplication.css';

const ReviewApplication = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [application, setApplication] = useState(null);
  const [criteria, setCriteria] = useState([]);
  const [currentRound, setCurrentRound] = useState('Round 1');
  const [activeTab, setActiveTab] = useState('1');
  const [scores, setScores] = useState({});
  const [criterionRemarks, setCriterionRemarks] = useState({});
  const [editableFinalScores, setEditableFinalScores] = useState({});
  const [finalRemarks, setFinalRemarks] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const sections = [
    { id: '1', name: 'Personal Information', key: 'personal' },
    { id: '2', name: 'Educational Background', key: 'education' },
    { id: '3', name: 'Professional Experience', key: 'experience' },
    { id: '4', name: 'Recommendations', key: 'recommendation' },
    { id: '5', name: 'Fellowship Information', key: 'fellowship' },
    { id: '6', name: 'Book Proposal', key: 'proposal' },
    { id: '7', name: 'Writing Sample', key: 'writing' },
    { id: '8', name: 'Publications', key: 'publications' },
    { id: '9', name: 'Final Review', key: 'final' },
  ];

  const fetchApplicationData = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/reviewer/applications/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApplication(response.data.data);
      setCurrentRound(response.data.data.currentRound || 'Round 1');
      setIsSubmitted(response.data.data.isSubmitted || false);
    } catch (error) {
      console.error('Error fetching application:', error);
      toast.error('Failed to load application');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchScoringCriteria = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/reviewer/criteria`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCriteria(response.data.data || []);
      const initialScores = {};
      response.data.data?.forEach(c => {
        initialScores[c.id] = 0;
      });
      setEditableFinalScores(initialScores);
    } catch (error) {
      console.error('Error fetching criteria:', error);
    }
  }, []);

  const fetchExistingReview = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/reviewer/applications/${id}/review`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        const { scores: existingScores, finalRemarks: existingFinalRemarks } = response.data.data;
        
        const scoreData = {};
        const editableScoreData = {};
        const criterionRemarksData = {};
        existingScores.forEach(score => {
          scoreData[score.criterion_id] = score.score;
          editableScoreData[score.criterion_id] = score.score;
          if (score.remarks) {
            criterionRemarksData[score.criterion_id] = score.remarks;
          }
        });
        setScores(scoreData);
        setEditableFinalScores(editableScoreData);
        setCriterionRemarks(criterionRemarksData);
        if (existingFinalRemarks) {
          setFinalRemarks(existingFinalRemarks);
        }
      }
    } catch (error) {
      console.error('Error fetching existing review:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchApplicationData();
    fetchScoringCriteria();
    fetchExistingReview();
  }, [fetchApplicationData, fetchScoringCriteria, fetchExistingReview]);

  const handleScoreChange = (criterionId, value) => {
    const numValue = parseFloat(value);
    const criterion = criteria.find(c => c.id === criterionId);
    
    if (numValue < criterion.min_score || numValue > criterion.max_score) {
      toast.warning(`Score must be between ${criterion.min_score} and ${criterion.max_score}`);
      return;
    }
    
    setScores(prev => ({
      ...prev,
      [criterionId]: numValue,
    }));
  };

  const handleEditableFinalScoreChange = (criterionId, value) => {
    const numValue = parseFloat(value);
    const criterion = criteria.find(c => c.id === criterionId);
    
    if (numValue < criterion.min_score || numValue > criterion.max_score) {
      toast.warning(`Score must be between ${criterion.min_score} and ${criterion.max_score}`);
      return;
    }
    
    setEditableFinalScores(prev => ({
      ...prev,
      [criterionId]: numValue,
    }));
  };



  const saveScores = async () => {
    try {
      setSaving(true);
      const token = sessionStorage.getItem('token');
      
      const scoresArray = criteria
        .filter(c => scores[c.id] > 0)
        .map(c => ({
          criterion_id: c.id,
          score: scores[c.id],
          remarks: criterionRemarks[c.id] || '',
        }));
      
      const payload = {
        round: currentRound,
        scores: scoresArray,
        finalRemarks: finalRemarks || '',
      };
      
      await axios.post(
        `${BASE_URL}/api/reviewer/applications/${id}/review`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setEditableFinalScores({...scores});
      toast.success('Scores and remarks saved successfully');
    } catch (error) {
      console.error('Error saving scores:', error);
      toast.error('Failed to save scores');
    } finally {
      setSaving(false);
    }
  };

  const moveToNextTab = () => {
    const currentIndex = sections.findIndex(s => s.id === activeTab);
    if (currentIndex < sections.length - 1) {
      setActiveTab(sections[currentIndex + 1].id);
    }
  };

  const submitReview = async () => {
    const missingScores = criteria.filter(c => !editableFinalScores[c.id] || editableFinalScores[c.id] <= 0);
    
    if (missingScores.length > 0) {
      toast.error('Please complete all scores before submitting');
      return;
    }

    const missingRemarks = criteria.filter(c => !criterionRemarks[c.id] || criterionRemarks[c.id].trim() === '');
    
    if (missingRemarks.length > 0) {
      toast.error('Please provide remarks for all scoring criteria before submitting');
      return;
    }
    
    try {
      setSaving(true);
      const token = sessionStorage.getItem('token');
      
      const scoresArray = criteria.map(c => ({
        criterion_id: c.id,
        score: editableFinalScores[c.id],
        remarks: criterionRemarks[c.id] || '',
      }));

      const savePayload = {
        round: currentRound,
        scores: scoresArray,
        finalRemarks: finalRemarks || '',
      };

      await axios.post(
        `${BASE_URL}/api/reviewer/applications/${id}/review`,
        savePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await axios.post(
        `${BASE_URL}/api/reviewer/applications/${id}/submit`,
        { round: currentRound },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Review submitted successfully');
      navigate('/reviewer/dashboard');
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.message || 'Failed to submit review');
    } finally {
      setSaving(false);
    }
  };

  const calculateTotalScore = () => {
    let totalScore = 0;
    
    criteria.forEach(c => {
      const score = editableFinalScores[c.id] || 0;
      const normalizedScore = (score / c.max_score) * 100;
      totalScore += normalizedScore * (c.weight / 100);
    });
    
    return totalScore;
  };

  // Rendering functions remain the same as backup
  const renderPersonalInfo = () => {
    if (!application?.user) return null;
    const { user, personalInfo } = application;
    
    return (
      <Card className="mb-3">
        <CardHeader className="bg-light">
          <h5>Personal Information</h5>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md={3}>
              {personalInfo?.photoUrl ? (
                <img
                  src={personalInfo.photoUrl}
                  alt="Profile"
                  className="img-thumbnail mb-3"
                  style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                />
              ) : (
                <div className="bg-secondary text-white d-flex align-items-center justify-content-center mb-3"
                  style={{ width: '150px', height: '150px' }}>
                  No Photo
                </div>
              )}
            </Col>
            <Col md={9}>
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <td className="fw-bold" width="30%">Full Name</td>
                    <td>{`${personalInfo?.firstName || ''} ${personalInfo?.middleName || ''} ${personalInfo?.lastName || ''}`}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Email Address</td>
                    <td>{user.email}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Phone Number</td>
                    <td>{personalInfo?.phone || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Date of Birth</td>
                    <td>{personalInfo?.dateOfBirth ? format(new Date(personalInfo.dateOfBirth), 'MMM dd, yyyy') : 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Gender</td>
                    <td>{personalInfo?.gender || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Nationality</td>
                    <td>{personalInfo?.nationality || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Mailing Address</td>
                    <td>
                      {personalInfo?.mailingAddress?.address}, {personalInfo?.mailingAddress?.city}, {personalInfo?.mailingAddress?.state}, {personalInfo?.mailingAddress?.country} - {personalInfo?.mailingAddress?.zipCode}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">CV/Resume</td>
                    <td>
                      {personalInfo?.documentUrl ? (
                        <a href={personalInfo.documentUrl} target="_blank" rel="noopener noreferrer">
                          <i className="bi bi-file-pdf"></i> View CV
                        </a>
                      ) : 'Not uploaded'}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
        </CardBody>
      </Card>
    );
  };

  const renderEducation = () => {
    if (!application?.education?.length) return <p>No education information provided</p>;
    
    return (
      <Card className="mb-3">
        <CardHeader className="bg-light">
          <h5>Educational Background</h5>
        </CardHeader>
        <CardBody>
          {application.education.map((edu, idx) => (
            <div key={idx} className={idx > 0 ? 'mt-3 pt-3 border-top' : ''}>
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <td className="fw-bold" width="30%">Degree</td>
                    <td>{edu.degree}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Field of Study</td>
                    <td>{edu.field_of_study}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Graduation Year</td>
                    <td>{edu.graduation_year}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Institution</td>
                    <td>{edu.institution_name}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Location</td>
                    <td>{`${edu.city || ''}, ${edu.state || ''}, ${edu.country || ''}`}</td>
                  </tr>
                  {edu.institution_webpage && (
                    <tr>
                      <td className="fw-bold">Institution Webpage</td>
                      <td>
                        <a href={edu.institution_webpage} target="_blank" rel="noopener noreferrer">
                          {edu.institution_webpage}
                        </a>
                      </td>
                    </tr>
                  )}
                  {edu.certificate_url && (
                    <tr>
                      <td className="fw-bold">Certificate</td>
                      <td>
                        <a href={edu.certificate_url} target="_blank" rel="noopener noreferrer">
                          <i className="bi bi-file-pdf"></i> View Certificate
                        </a>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          ))}
        </CardBody>
      </Card>
    );
  };

  const renderExperience = () => {
    if (!application?.professional_experience?.length) return <p>No professional experience provided</p>;
    
    return (
      <Card className="mb-3">
        <CardHeader className="bg-light">
          <h5>Professional Experience</h5>
        </CardHeader>
        <CardBody>
          {application.professional_experience.map((exp, idx) => (
            <div key={idx} className={idx > 0 ? 'mt-3 pt-3 border-top' : ''}>
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <td className="fw-bold" width="30%">Job Title / Position</td>
                    <td>{exp.designation}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Organization / Company</td>
                    <td>{exp.organisation_name}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Job Description</td>
                    <td>{exp.responsibilities}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          ))}
        </CardBody>
      </Card>
    );
  };

  const renderRecommendations = () => {
    if (!application?.referees?.length) return <p>No recommendations provided</p>;
    
    return (
      <Card className="mb-3">
        <CardHeader className="bg-light">
          <h5>Recommendations</h5>
        </CardHeader>
        <CardBody>
          {application.referees.map((ref, idx) => (
            <div key={idx} className={idx > 0 ? 'mt-3 pt-3 border-top' : ''}>
              <h6 className="text-primary">Referee {idx + 1}</h6>
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <td className="fw-bold" width="30%">Name</td>
                    <td>{ref.name}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Email</td>
                    <td>{ref.email}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Phone</td>
                    <td>{ref.mobile}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Designation</td>
                    <td>{ref.designation}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Institution</td>
                    <td>{ref.institution}</td>
                  </tr>
                  {ref.institution_webpage && (
                    <tr>
                      <td className="fw-bold">Institution Webpage</td>
                      <td>
                        <a href={ref.institution_webpage} target="_blank" rel="noopener noreferrer">
                          {ref.institution_webpage}
                        </a>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="fw-bold">Response Status</td>
                    <td>
                      <Badge color={ref.response === 'Yes' ? 'success' : ref.response === 'No' ? 'danger' : 'warning'}>
                        {ref.response || 'Pending'}
                      </Badge>
                    </td>
                  </tr>
                  {ref.letter_url && (
                    <tr>
                      <td className="fw-bold">Recommendation Letter</td>
                      <td>
                        <a href={ref.letter_url} target="_blank" rel="noopener noreferrer">
                          <i className="bi bi-file-pdf"></i> View Letter
                        </a>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          ))}
        </CardBody>
      </Card>
    );
  };

  const renderFellowshipInfo = () => {
    if (!application?.fellowshipInfo) return <p>No fellowship information provided</p>;
    const { fellowshipInfo } = application;
    
    return (
      <Card className="mb-3">
        <CardHeader className="bg-light">
          <h5>Fellowship Information</h5>
        </CardHeader>
        <CardBody>
          <Table bordered size="sm">
            <tbody>
              <tr>
                <td className="fw-bold" width="30%">Program Applied For</td>
                <td>{fellowshipInfo.program}</td>
              </tr>
              <tr>
                <td className="fw-bold">Selected Themes</td>
                <td>
                  {Array.isArray(fellowshipInfo.themes) 
                    ? fellowshipInfo.themes.join(', ')
                    : fellowshipInfo.themes}
                  {fellowshipInfo.other_option && ` (Other: ${fellowshipInfo.other_option})`}
                </td>
              </tr>
              {fellowshipInfo.book_proposal_url && (
                <tr>
                  <td className="fw-bold">Book Proposal</td>
                  <td>
                    <a href={fellowshipInfo.book_proposal_url} target="_blank" rel="noopener noreferrer">
                      <i className="bi bi-file-pdf"></i> View Book Proposal
                    </a>
                  </td>
                </tr>
              )}
              {fellowshipInfo.writing_sample_url && (
                <tr>
                  <td className="fw-bold">Writing Sample</td>
                  <td>
                    <a href={fellowshipInfo.writing_sample_url} target="_blank" rel="noopener noreferrer">
                      <i className="bi bi-file-pdf"></i> View Writing Sample
                    </a>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    );
  };

  const renderPublications = () => {
    if (!application?.additionalInfo?.publications?.length) return <p>No publications provided</p>;
    
    return (
      <Card className="mb-3">
        <CardHeader className="bg-light">
          <h5>Publications</h5>
        </CardHeader>
        <CardBody>
          {application.additionalInfo.publications.map((pub, idx) => (
            <div key={idx} className={idx > 0 ? 'mt-3 pt-3 border-top' : ''}>
              <h6 className="text-primary">Publication {idx + 1}</h6>
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <td className="fw-bold" width="30%">Title</td>
                    <td>{pub.title}</td>
                  </tr>
                  {pub.authors && (
                    <tr>
                      <td className="fw-bold">Authors</td>
                      <td>{pub.authors}</td>
                    </tr>
                  )}
                  {pub.journal && (
                    <tr>
                      <td className="fw-bold">Journal/Conference</td>
                      <td>{pub.journal}</td>
                    </tr>
                  )}
                  {pub.year && (
                    <tr>
                      <td className="fw-bold">Year</td>
                      <td>{pub.year}</td>
                    </tr>
                  )}
                  {pub.url && (
                    <tr>
                      <td className="fw-bold">URL</td>
                      <td>
                        <a href={pub.url} target="_blank" rel="noopener noreferrer">
                          {pub.url}
                        </a>
                      </td>
                    </tr>
                  )}
                  {pub.file_url && (
                    <tr>
                      <td className="fw-bold">File</td>
                      <td>
                        <a href={pub.file_url} target="_blank" rel="noopener noreferrer">
                          <i className="bi bi-file-pdf"></i> View Publication
                        </a>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          ))}
        </CardBody>
      </Card>
    );
  };

  const renderNavigationActions = () => {
    return (
      <div className="d-flex justify-content-end gap-2 mb-3">
        <Button 
          color="success" 
          onClick={moveToNextTab}
          disabled={activeTab === '9'}
        >
          Next <i className="bi bi-arrow-right ms-1"></i>
        </Button>
      </div>
    );
  };

  const renderScoringSection = () => {
    return (
      <Card className="mb-3 border-info scoring-section">
        <CardHeader className="bg-info text-white">
          <h5 className="mb-0">
            <i className="bi bi-clipboard-data me-2"></i>
            Scoring Section (Common for All Tabs)
          </h5>
        </CardHeader>
        <CardBody>
          <Alert color="info" className="mb-3">
            <i className="bi bi-info-circle me-2"></i>
            This scoring section is common across all tabs. You can update scores anytime and they will be saved together.
          </Alert>

          {criteria.map(criterion => (
            <Card key={criterion.id} className="mb-3 border">
              <CardBody>
                <Row>
                  <Col md={8}>
                    <h6 className="text-primary">{criterion.name}</h6>
                    <p className="text-muted small mb-2">{criterion.description}</p>
                    <p className="small mb-0">
                      <strong>Weight:</strong> {criterion.weight}% | 
                      <strong> Range:</strong> {criterion.min_score} - {criterion.max_score}
                    </p>
                    {criterion.rubric_text && (
                      <div className="mt-2">
                        <small className="text-muted">
                          <strong>Rubric:</strong>
                          <pre className="mt-1 p-2 bg-light rounded" style={{ fontSize: '0.75rem' }}>
                            {criterion.rubric_text}
                          </pre>
                        </small>
                      </div>
                    )}
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label className="fw-bold">Score</Label>
                      <Input
                        type="number"
                        min={criterion.min_score}
                        max={criterion.max_score}
                        step="0.1"
                        value={scores[criterion.id] || ''}
                        onChange={(e) => handleScoreChange(criterion.id, e.target.value)}
                        placeholder={`${criterion.min_score}-${criterion.max_score}`}
                        className="score-input"
                        disabled={isSubmitted}
                      />
                      <small className="text-muted">
                        Max: {criterion.max_score}
                      </small>
                    </FormGroup>
                    <FormGroup className="mt-3">
                      <Label className="fw-bold">
                        Remarks/Feedback <span className="text-danger">(Mandatory)</span>
                      </Label>
                      <Input
                        type="textarea"
                        rows="3"
                        value={criterionRemarks[criterion.id] || ''}
                        onChange={(e) => setCriterionRemarks({
                          ...criterionRemarks,
                          [criterion.id]: e.target.value
                        })}
                        placeholder="Enter specific feedback or remarks for this criterion... (Required)"
                        disabled={isSubmitted}
                        className="remarks-textarea"
                        required
                      />
                      {!isSubmitted && (!criterionRemarks[criterion.id] || criterionRemarks[criterion.id].trim() === '') && (
                        <small className="text-danger">
                          <i className="bi bi-exclamation-circle me-1"></i>
                          Remarks are required for this criterion
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          ))}

          {!isSubmitted && (
            <div className="text-center mt-3">
              <Button 
                color="primary" 
                size="lg"
                onClick={saveScores} 
                disabled={saving || Object.keys(scores).length === 0}
              >
                {saving ? <><Spinner size="sm" className="me-2" /> Saving Scores...</> : <><i className="bi bi-save me-2"></i>Save All Scores</>}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    );
  };

  const renderFinalReview = () => {
    const totalScore = calculateTotalScore();
    const allScoresProvided = criteria.every(c => editableFinalScores[c.id] > 0);
    
    return (
      <div>
        <Card className="mb-3">
          <CardHeader className="bg-success text-white">
            <h5 className="mb-0">
              <i className="bi bi-check-circle me-2"></i>
              Final Review & Submission
            </h5>
          </CardHeader>
          <CardBody>
            <Alert color="warning">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>Important:</strong> You can edit the final scores below before submitting. Review all scores carefully.
            </Alert>

            <h6 className="mb-3">Edit Final Scores & Review Remarks</h6>
            <Table bordered responsive className="mb-4">
              <thead className="table-light">
                <tr>
                  <th>Criterion</th>
                  <th width="10%">Weight</th>
                  <th width="10%">Max Score</th>
                  <th width="12%">Your Score</th>
                  <th width="12%">Normalized</th>
                  <th width="40%">Your Remarks</th>
                </tr>
              </thead>
              <tbody>
                {criteria.map(c => {
                  const score = editableFinalScores[c.id] || 0;
                  const normalized = score > 0 ? (score / c.max_score) * 100 : 0;
                  
                  return (
                    <tr key={c.id}>
                      <td className="fw-bold">{c.name}</td>
                      <td>{c.weight}%</td>
                      <td>{c.max_score}</td>
                      <td>
                        <Input
                          type="number"
                          min={c.min_score}
                          max={c.max_score}
                          step="0.1"
                          value={score}
                          onChange={(e) => handleEditableFinalScoreChange(c.id, e.target.value)}
                          className="score-input"
                          bsSize="sm"
                          disabled={isSubmitted}
                        />
                      </td>
                      <td className="text-center">
                        <Badge color={normalized >= 70 ? 'success' : normalized >= 50 ? 'warning' : 'danger'}>
                          {normalized.toFixed(1)}%
                        </Badge>
                      </td>
                      <td>
                        <small style={{ whiteSpace: 'pre-wrap', display: 'block' }}>
                          {criterionRemarks[c.id] || <span className="text-muted">No remarks provided</span>}
                        </small>
                      </td>
                    </tr>
                  );
                })}
                <tr className="table-success fw-bold">
                  <td colSpan="4" className="text-end">Total Weighted Score:</td>
                  <td className="text-center">
                    <h5 className="mb-0">
                      <Badge color={totalScore >= 70 ? 'success' : totalScore >= 50 ? 'warning' : 'danger'} className="px-3 py-2">
                        {totalScore.toFixed(2)}%
                      </Badge>
                    </h5>
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </Table>

            <Row className="mb-4">
              <Col>
                <div className="text-center">
                  <h6>Overall Score Visualization</h6>
                  <Progress 
                    value={totalScore} 
                    color={totalScore >= 70 ? 'success' : totalScore >= 50 ? 'warning' : 'danger'} 
                    style={{ height: '30px' }}
                    className="mb-2"
                  >
                    <span className="fw-bold">{totalScore.toFixed(2)}%</span>
                  </Progress>
                  <small className="text-muted">
                    {totalScore >= 70 ? '✓ Strong Candidate' : totalScore >= 50 ? '⚠ Moderate Candidate' : '✗ Weak Candidate'}
                  </small>
                </div>
              </Col>
            </Row>

            <Card className="mb-4 border">
              <CardBody>
                <FormGroup>
                  <Label className="fw-bold">
                    <i className="bi bi-chat-text me-2"></i>
                    Final Overall Remarks (Optional)
                  </Label>
                  <Input
                    type="textarea"
                    rows="5"
                    value={finalRemarks}
                    onChange={(e) => setFinalRemarks(e.target.value)}
                    placeholder="Provide your overall assessment and final remarks about the candidate's application..."
                    disabled={isSubmitted}
                    className="remarks-textarea"
                  />
                  <small className="text-muted">
                    This is your opportunity to provide an overall assessment beyond the individual criterion remarks.
                  </small>
                </FormGroup>
              </CardBody>
            </Card>

            <div className="alert alert-info mt-4">
              <h6>Before Submitting:</h6>
              <ul className="mb-0">
                <li>✓ All criteria scores have been provided and reviewed</li>
                <li>✓ Final scores have been edited if necessary</li>
                <li>✓ Criterion-specific remarks are mandatory for all criteria</li>
                <li>✓ All sections have been reviewed thoroughly</li>
              </ul>
            </div>
            
            <div className="d-flex justify-content-between mt-4">
              <Button color="secondary" onClick={() => navigate('/reviewer/dashboard')}>
                <i className="bi bi-arrow-left me-2"></i>
                Back to Dashboard
              </Button>
              {!isSubmitted && (
                <Button 
                  color="success" 
                  size="lg"
                  onClick={submitReview} 
                  disabled={!allScoresProvided || saving}
                >
                  {saving ? (
                    <><Spinner size="sm" className="me-2" /> Submitting...</>
                  ) : (
                    <><i className="bi bi-send-check me-2"></i>Submit Final Review</>
                  )}
                </Button>
              )}
            </div>
          </CardBody>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
        <p className="mt-3">Loading application...</p>
      </Container>
    );
  }

  if (!application) {
    return (
      <Container className="text-center py-5">
        <h4>Application not found</h4>
        <Button color="primary" onClick={() => navigate('/reviewer/dashboard')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const totalScore = calculateTotalScore();
  
  return (
    <div className="review-application-page">
      <Container>
        <Card className="mb-4 border-primary shadow-sm">
          <CardBody>
            <Row className="align-items-center mb-3">
              <Col xs="auto">
                <Button color="secondary" outline onClick={() => navigate('/reviewer/dashboard')}>
                  <i className="bi bi-arrow-left"></i> Back
                </Button>
              </Col>
              <Col>
                <h4 className="mb-1">Review Application</h4>
                <p className="text-muted mb-0 small">
                  Application ID: <strong>#{id}</strong> | Round: <Badge color="info">{currentRound}</Badge>
                  {isSubmitted && <Badge color="success" className="ms-2"><i className="bi bi-check-circle me-1"></i>Submitted</Badge>}
                </p>
              </Col>
            </Row>
            {isSubmitted && (
              <Alert color="success" className="mb-3">
                <i className="bi bi-check-circle-fill me-2"></i>
                <strong>Review Submitted:</strong> This review has been submitted and can no longer be edited.
              </Alert>
            )}
            <hr className="my-3" />
            <Row className="align-items-center">
              <Col md={6}>
                <h5 className="mb-1 text-primary">{application.user?.fullName}</h5>
                <p className="text-muted mb-0">
                  <i className="bi bi-envelope me-2"></i>
                  {application.user?.email}
                </p>
              </Col>
              <Col md={3}>
                <div>
                  <small className="text-muted d-block mb-1">Program Applied</small>
                  <Badge color="primary" className="p-2">{application.fellowshipInfo?.program}</Badge>
                </div>
              </Col>
              <Col md={3} className="text-end">
                <div>
                  <small className="text-muted d-block mb-1">Current Score</small>
                  <h4 className="mb-0">
                    <Badge color={totalScore >= 70 ? 'success' : totalScore >= 50 ? 'warning' : 'secondary'} className="px-3 py-2">
                      {totalScore.toFixed(1)}%
                    </Badge>
                  </h4>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Nav tabs className="mb-3 custom-tabs">
          {sections.map((section, index) => {
            const isActive = activeTab === section.id;
            const currentIndex = sections.findIndex(s => s.id === activeTab);
            const isAccessible = index <= currentIndex + 1;
            
            return (
              <NavItem key={section.id}>
                <NavLink
                  className={`${isActive ? 'active' : ''} ${!isAccessible ? 'disabled' : ''}`}
                  onClick={() => isAccessible && setActiveTab(section.id)}
                  style={{ 
                    cursor: isAccessible ? 'pointer' : 'not-allowed',
                    opacity: isAccessible ? 1 : 0.5,
                  }}
                  disabled={!isAccessible}
                >
                  {section.name}
                  {isActive && (
                    <Badge color="primary" className="ms-2">Current</Badge>
                  )}
                </NavLink>
              </NavItem>
            );
          })}
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="1">
            {renderPersonalInfo()}
            {renderNavigationActions()}
            {renderScoringSection()}
          </TabPane>
          
          <TabPane tabId="2">
            {renderEducation()}
            {renderNavigationActions()}
            {renderScoringSection()}
          </TabPane>
          
          <TabPane tabId="3">
            {renderExperience()}
            {renderNavigationActions()}
            {renderScoringSection()}
          </TabPane>
          
          <TabPane tabId="4">
            {renderRecommendations()}
            {renderNavigationActions()}
            {renderScoringSection()}
          </TabPane>
          
          <TabPane tabId="5">
            {renderFellowshipInfo()}
            {renderNavigationActions()}
            {renderScoringSection()}
          </TabPane>
          
          <TabPane tabId="6">
            {application?.fellowshipInfo?.book_proposal_url && (
              <Card className="mb-3">
                <CardHeader className="bg-light">
                  <h5>Book Proposal Document</h5>
                </CardHeader>
                <CardBody className="text-center">
                  <a href={application.fellowshipInfo.book_proposal_url} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
                    <i className="bi bi-file-pdf me-2"></i> Open Book Proposal
                  </a>
                </CardBody>
              </Card>
            )}
            {renderNavigationActions()}
            {renderScoringSection()}
          </TabPane>
          
          <TabPane tabId="7">
            {application?.fellowshipInfo?.writing_sample_url && (
              <Card className="mb-3">
                <CardHeader className="bg-light">
                  <h5>Writing Sample Document</h5>
                </CardHeader>
                <CardBody className="text-center">
                  <a href={application.fellowshipInfo.writing_sample_url} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
                    <i className="bi bi-file-pdf me-2"></i> Open Writing Sample
                  </a>
                </CardBody>
              </Card>
            )}
            {renderNavigationActions()}
            {renderScoringSection()}
          </TabPane>
          
          <TabPane tabId="8">
            {renderPublications()}
            {renderNavigationActions()}
            {renderScoringSection()}
          </TabPane>
          
          <TabPane tabId="9">
            {renderFinalReview()}
          </TabPane>
        </TabContent>

        {saving && (
          <div className="position-fixed bottom-0 end-0 m-3" style={{ zIndex: 1050 }}>
            <Alert color="info" className="mb-0 shadow-lg">
              <Spinner size="sm" className="me-2" /> Saving...
            </Alert>
          </div>
        )}
      </Container>
    </div>
  );
};

export default ReviewApplication;
